<script setup lang="ts">
import Carousel from '@ui/components/carousel/Carousel.vue'
import IconButton from '@ui/components/icon/IconButton.vue'
import Image from '@ui/components/image/Image.vue'
import Modal from '@ui/components/modal/Modal.vue'
import { ButtonVariant, Sizes } from '@ui/styles'
import { DVH } from '@ui/utils/supportsCSS'
import { useEventListener } from '@vueuse/core'
import Fade from 'embla-carousel-fade'
import { computed, ref, watch } from 'vue'
import ArrowLeft from '~icons/cue-filled/arrow-left'
import ArrowRight from '~icons/cue-filled/arrow-right'

const props = withDefaults(
  defineProps<{
    src?: string[]
  }>(),
  {
    src: () => [],
  },
)

const show = defineModel<boolean>('show')
const carousel = ref<InstanceType<typeof Carousel>>()
const api = computed(() => carousel.value?.api)
const plugins = [Fade()]
const selectedIndex = ref(0)

watch(show, isOpen => {
  if (isOpen) {
    api.value?.goTo(0, true)
  }
})

watch(api, (instance, _, onCleanup) => {
  selectedIndex.value = instance?.selectedSnap() ?? 0
  if (!instance) {
    return
  }

  const updateSelection = () => {
    selectedIndex.value = instance.selectedSnap()
  }
  instance.on('select', updateSelection)
  instance.on('reinit', updateSelection)
  onCleanup(() => {
    instance.off('select', updateSelection)
    instance.off('reinit', updateSelection)
  })
})

useEventListener('keydown', (event: KeyboardEvent) => {
  if (
    !show.value
    || !api.value
    || props.src.length < 2
    || event.defaultPrevented
    || event.altKey
    || event.ctrlKey
    || event.metaKey
    || event.shiftKey
  ) {
    return
  }
  if (
    event.target instanceof Element
    && event.target.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"])')
  ) {
    return
  }
  if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
    return
  }

  event.preventDefault()
  if (event.key === 'ArrowLeft') {
    api.value.goToPrev()
  } else {
    api.value.goToNext()
  }
})
</script>

<template lang="pug">
Modal(v-model:show="show" :maxWidth="`800px, 100${DVH} - 16px`" useVShow)
  template(#content)
    .flex.overflow-hidden.col-16.p-12-24-20.w-100p.min-w-0
      .flex.row-12.overflow-hidden.align-items-center
        IconButton.icon-button(v-if="src.length > 1" :size="Sizes.Luke" :variant="ButtonVariant.Ghost" :name="ArrowLeft" aria-label="Previous image" @click="api?.goToPrev()")
        Carousel.slider.flex-1(ref="carousel" :options="{ loop: true, containScroll: false }" :plugins="plugins")
          Image.slide-image.w-100p(v-for="(image, index) in src" :key="`${index}:${image}`" :src="image" :contain="false")
        IconButton.icon-button(v-if="src.length > 1" :size="Sizes.Luke" :variant="ButtonVariant.Ghost" :name="ArrowRight" aria-label="Next image" @click="api?.goToNext()")
      .pagination.flex-center-center.row-8.min-h-18.flex-wrap(v-if="src.length > 1")
        button.pagination-dot.s-10.br-10.p-0.flex-shrink-0.cursor-pointer(
          v-for="(_, index) in src"
          :key="index"
          type="button"
          :class="{ active: selectedIndex === index }"
          :aria-label="`${index + 1} / ${src.length}`"
          :aria-current="selectedIndex === index ? 'true' : undefined"
          @click="api?.goTo(index)"
        )
</template>

<style scoped>
.pagination-dot {
  border: 0;
  background: var(--cue-border-neutral-tertiary);

  &.active {
    background: var(--cue-content-primary-default);
  }
}

.icon-button {
  @screen lt-md {
    display: none;
  }
}

.slider,
.slide-image {
  aspect-ratio: 1;
}
</style>
